import { Button, ButtonGroup, Heading, IconButton, Modal, TextButton } from '@zillow/constellation-10';
import { IconQuestionMarkCircleFilled } from '@zillow/constellation-icons';
import { SubmitRequestPageComponent } from './SubmitRequestComponent';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import { floatingWidgetStyles } from './styles';

/** Location shape compatible with react-router and Next.js (pathname + search). */
export type RequestWidgetLocation = { pathname: string; search: string };

// Request Widget will open automatically for urls where `?${SUPPORT_PARAM}=true'
const SUPPORT_PARAM = 'support';

interface SubmitRequestModalPageFooterProps {
  onClick: () => void;
  disabled: boolean;
  onCancel?: () => void;
  customSubmitText?: string | undefined;
}

export const SubmitRequestModalPageFooter = ({
  onClick,
  disabled,
  onCancel,
  customSubmitText,
}: SubmitRequestModalPageFooterProps) => (
  <ButtonGroup aria-label="Modal actions">
    {onCancel !== undefined && !customSubmitText && <TextButton onClick={onCancel}>Cancel</TextButton>}
    <Button tone="brand" emphasis="filled" onClick={onClick} disabled={disabled}>
      {customSubmitText ? customSubmitText : 'Submit'}
    </Button>
  </ButtonGroup>
);

type RequestWidgetProps = {
  zrmwUser?: RentalManagerAuthCheckUserInfoWebApiDto;
  /**
   * Optional location from Next.js (e.g. from useRouter() + usePathname() / searchParams).
   * When provided, useLocation() is not called — use this when rendering outside react-router.
   */
  routerLocation?: RequestWidgetLocation;
};

function RequestWidgetInner({
  location,
  zrmwUser,
}: {
  location: RequestWidgetLocation;
  zrmwUser?: RentalManagerAuthCheckUserInfoWebApiDto;
}) {
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [hasClosedWidget, setHasClosedWidget] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const params = new URLSearchParams(location.search);
  const forceOpenWidget = !hasClosedWidget && params.get(SUPPORT_PARAM) === 'true';

  const isListingPage = (pathname: string) => {
    const listingPageRegex = new RegExp('/rental-manager/properties/[a-z0-9]*/listing');
    return pathname.match(listingPageRegex);
  };

  const onModalClose = () => {
    setHasClosedWidget(true);
    setSubmitClicked(false);
    setIsOpen(false);
  };

  const onNext = !submitClicked ? () => setSubmitClicked(true) : () => onModalClose();

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setIsOpen(true);
    } else {
      onModalClose();
    }
  };

  return (
    <Modal
      open={isOpen || forceOpenWidget}
      onOpenChange={handleOpenChange}
      size={{ base: 'fullScreen', md: 'lg' }}
      trigger={
        <IconButton
          size="lg"
          tone="brand"
          emphasis="outlined"
          icon={<IconQuestionMarkCircleFilled />}
          shape="circle"
          css={{
            ...floatingWidgetStyles,
            ...(isListingPage(location.pathname) ? { marginBottom: { base: '15vh', md: '10vh' } } : {}),
          }}
          title="Need support"
        />
      }
      header={<Heading level={1}>Support request</Heading>}
      body={
        <SubmitRequestPageComponent
          zrmwUser={zrmwUser}
          setSubmitClicked={setSubmitClicked}
          submitClicked={submitClicked}
          setCanSubmit={setCanSubmit}
        />
      }
      footer={
        <SubmitRequestModalPageFooter
          onClick={onNext}
          disabled={!canSubmit}
          onCancel={onModalClose}
          customSubmitText={submitClicked ? 'Next' : undefined}
        />
      }
    />
  );
}

function RequestWidgetWithRouter({ zrmwUser }: { zrmwUser?: RentalManagerAuthCheckUserInfoWebApiDto }) {
  const location = useLocation();
  return <RequestWidgetInner location={{ pathname: location.pathname, search: location.search }} zrmwUser={zrmwUser} />;
}

export const RequestWidget = ({ zrmwUser, routerLocation }: RequestWidgetProps) => {
  if (routerLocation !== undefined) {
    return <RequestWidgetInner location={routerLocation} zrmwUser={zrmwUser} />;
  }
  return <RequestWidgetWithRouter zrmwUser={zrmwUser} />;
};
